import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { ArrowRight, Truck, Shield, Clock, Headphones, Star, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Design guidelines images
const HERO_IMAGE = "https://images.unsplash.com/photo-1758548157243-f4ef3e614684?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB3aGl0ZSUyMGtpdGNoZW4lMjBjYWJpbmV0c3xlbnwwfHx8fDE3NzAxNjg3NDd8MA&ixlib=rb-4.1.0&q=85";

const COLLECTIONS = [
  {
    url: "https://images.unsplash.com/photo-1653087881002-071d4c840f9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3aGl0ZSUyMGtpdGNoZW4lMjBjYWJpbmV0c3xlbnwwfHx8fDE3NzAxNjg3NDd8MA&ixlib=rb-4.1.0&q=85",
    alt: "Modern White Collection",
    category: "Modern White",
    description: "Clean lines, timeless elegance"
  },
  {
    url: "https://images.unsplash.com/photo-1758548157275-d939cf0f0e32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB3b29kJTIwa2l0Y2hlbiUyMGNhYmluZXRzfGVufDB8fHx8MTc3MDE2ODc1M3ww&ixlib=rb-4.1.0&q=85",
    alt: "Luxury Wood Collection",
    category: "Luxury Wood",
    description: "Natural beauty, premium craftsmanship"
  },
  {
    url: "https://images.unsplash.com/photo-1765766601532-90e9b96320c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3b29kJTIwa2l0Y2hlbiUyMGNhYmluZXRzfGVufDB8fHx8MTc3MDE2ODc1M3ww&ixlib=rb-4.1.0&q=85",
    alt: "Dark & Moody Collection",
    category: "Noir Series",
    description: "Bold statements, dramatic impact"
  }
];

const FEATURES = [
  {
    icon: Shield,
    title: "Quality Materials",
    description: "Premium solid wood construction with superior finishes that last for decades."
  },
  {
    icon: Truck,
    title: "Fast Shipping",
    description: "Free delivery on orders over $2,500. Most orders ship within 5-7 business days."
  },
  {
    icon: Clock,
    title: "Easy Assembly",
    description: "RTA cabinets designed for simple installation. Everything you need included."
  },
  {
    icon: Headphones,
    title: "Expert Support",
    description: "Dedicated design consultants ready to help plan your dream kitchen."
  }
];

const TESTIMONIALS = [
  {
    name: "Sarah Mitchell",
    location: "Toronto, ON",
    rating: 5,
    text: "The quality exceeded my expectations. The shaker cabinets transformed our kitchen completely. Excellent customer service throughout the entire process."
  },
  {
    name: "Michael Chen",
    location: "Vancouver, BC",
    rating: 5,
    text: "Best decision we made for our renovation. The cabinets arrived quickly and the installation was straightforward. Highly recommend Kitchen99!"
  },
  {
    name: "Emily Rodriguez",
    location: "Calgary, AB",
    rating: 5,
    text: "From design consultation to delivery, the experience was seamless. Our kitchen looks like it belongs in a magazine. Thank you, Kitchen99!"
  }
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setFeaturedProducts(response.data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="hero-section grain" 
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        data-testid="hero-section"
      >
        <div className="hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl animate-fade-in-up">
            <span className="inline-block bg-[#8B5E3C] text-white text-sm font-medium px-4 py-2 mb-6 uppercase tracking-wider">
              Premium Quality Cabinets
            </span>
            <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              Premium Kitchen Cabinets, Built for Your Home
            </h1>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Transform your kitchen with our handcrafted cabinets. Quality materials, competitive pricing, and fast delivery across Canada.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-white text-[#1A1A1A] hover:bg-[#F9F7F2] px-8"
                asChild
              >
                <Link to="/shop" data-testid="hero-shop-btn">
                  Shop Cabinets
                  <ArrowRight className="ml-2" size={18} />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#1A1A1A] px-8"
                asChild
              >
                <Link to="/quote" data-testid="hero-quote-btn">
                  Get a Free Quote
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Collections Bento Grid */}
      <section className="py-20 bg-white" data-testid="collections-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
              Featured Collections
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto">
              Explore our curated cabinet collections, designed for every style and budget
            </p>
          </div>

          <div className="bento-grid">
            {/* Large item */}
            <Link
              to="/shop"
              className="bento-item-large relative overflow-hidden group"
              data-testid="collection-modern-white"
            >
              <img
                src={COLLECTIONS[0].url}
                alt={COLLECTIONS[0].alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                <span className="text-[#8B5E3C] text-sm font-medium uppercase tracking-wider">Collection</span>
                <h3 className="font-manrope text-2xl sm:text-3xl font-bold text-white mt-2">{COLLECTIONS[0].category}</h3>
                <p className="text-white/80 mt-2">{COLLECTIONS[0].description}</p>
                <span className="inline-flex items-center text-white mt-4 group-hover:text-[#8B5E3C] transition-colors">
                  Explore <ChevronRight size={18} className="ml-1" />
                </span>
              </div>
            </Link>

            {/* Medium items */}
            {COLLECTIONS.slice(1).map((collection, index) => (
              <Link
                key={collection.category}
                to="/shop"
                className="relative overflow-hidden group"
                data-testid={`collection-${collection.category.toLowerCase().replace(' ', '-')}`}
              >
                <img
                  src={collection.url}
                  alt={collection.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-4 sm:p-6">
                  <h3 className="font-manrope text-lg sm:text-xl font-bold text-white">{collection.category}</h3>
                  <span className="inline-flex items-center text-white/80 mt-2 text-sm group-hover:text-[#8B5E3C] transition-colors">
                    Shop Now <ChevronRight size={16} className="ml-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-[#F9F7F2]" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
            <div className="animate-fade-in-up">
              <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-2">
                Popular Cabinets
              </h2>
              <p className="text-[#6B7280]">
                Our best-selling kitchen cabinet styles
              </p>
            </div>
            <Button variant="outline" className="mt-4 sm:mt-0" asChild>
              <Link to="/shop" data-testid="view-all-products-btn">
                View All Products
                <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="product-grid">
              {featuredProducts.map((product, index) => (
                <div key={product.id} className={`animate-fade-in-up stagger-${index + 1}`}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
              Why Choose Kitchen99.ca
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto">
              We're committed to providing the best kitchen cabinet experience from selection to installation
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map((feature, index) => (
              <div
                key={feature.title}
                className={`feature-card animate-fade-in-up stagger-${index + 1}`}
                data-testid={`feature-${feature.title.toLowerCase().replace(' ', '-')}`}
              >
                <feature.icon size={32} className="text-[#8B5E3C] mb-4" />
                <h3 className="font-manrope text-lg font-semibold text-[#1A1A1A] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#6B7280] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#1A1A1A]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mb-4">
              What Our Customers Say
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Join thousands of satisfied homeowners who transformed their kitchens with Kitchen99
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <div
                key={testimonial.name}
                className={`testimonial-card animate-fade-in-up stagger-${index + 1}`}
                data-testid={`testimonial-${index + 1}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#8B5E3C] text-[#8B5E3C]" />
                  ))}
                </div>
                <p className="text-[#4B5563] mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-semibold text-[#1A1A1A]">{testimonial.name}</p>
                  <p className="text-sm text-[#6B7280]">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#8B5E3C]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mb-4 animate-fade-in-up">
            Ready to Transform Your Kitchen?
          </h2>
          <p className="text-white/90 text-lg mb-8 animate-fade-in-up stagger-1">
            Get a free design consultation and personalized quote from our kitchen experts.
          </p>
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up stagger-2">
            <Button
              size="lg"
              className="bg-white text-[#1A1A1A] hover:bg-[#F9F7F2] px-8"
              asChild
            >
              <Link to="/quote" data-testid="cta-quote-btn">
                Start Your Free Design
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#8B5E3C] px-8"
              asChild
            >
              <Link to="/contact" data-testid="cta-contact-btn">
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
